import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  ChevronRight, ClipboardCheck, GripVertical, Plus, Trash2, X, Check, Info, Users,
} from '../../icons';
// 채택 3단계 — 검색칸·고르개·칸·숫자·시각·단추를 정본 공통 컴포넌트로.
import {
  Button, FormField, Input, NumberInput, SearchInput, Select, Textarea, TimePicker,
} from '../../components/shared';
import {
  listReservationResources, createReservationResource, updateReservationResource,
  deleteReservationResource,
  type ReservationResource, type ReservationResourceInput, type ReservationManager,
} from '../../api/admin-reservations';
import { listDivisions, listTeams, listTeamMembers, type OrgDivision, type OrgTeam } from '../../api/admin-org';
import { listUsers, type UserListItem } from '../../api/admin-user';
import '../AttendancePage/UserManage.css';
import './console-shared.css';
import './ReservationWorkspace.css';

/**
 * 예약관리 — 좌 분류·자원 트리 / 우 인라인 편집 (2026-08-20).
 *
 * 시안 SSOT: docs/reservation-admin-sample.html
 * 디자인 SSOT: ./UserWorkspace.css 의 `um-*` (사용자 관리 · 워크스페이스 관리와 같은 2패널).
 *
 * 옛 화면(ReservationAdminSection.tsx — 카드 격자 + 12필드 모달)은 **무수정 보존**한다.
 * 롤백은 ConsolePage.tsx 의 import/렌더 2줄을 되돌리면 끝난다.
 *
 * 이 화면이 옛 화면 대비 실제로 하는 일:
 *   · sortOrder 를 화면에서 매긴다(끌어 옮기기 + 숫자 입력) — API 에 있는데 안 쓰고 있었다.
 *   · 활성/비활성을 헤더 토글로 즉시 바꾼다(모달을 열 필요가 없다).
 *   · 담당자를 조직도(부서 › 팀 › 팀원)에서 고른다 — listUsers 는 검색이 없어
 *     사용자 100명을 넘으면 옛 화면에서는 지정할 방법이 아예 없었다.
 *   · 사용자 화면(8443 예약하기) 카드 미리보기를 저장 전에 보여 준다.
 *
 * 저장 위치는 chat-server(prisma `reservation_resources`) — BFF 는 proxy 만 한다.
 */

const CATEGORY_PRESETS = ['회의실', '빔프로젝터', '도서대여', '개인장비', '차량'];

/** 분류 식별색 — 사용자 관리·워크스페이스 관리와 같은 accent 팔레트/같은 해시 규칙. */
const HUES = [
  'var(--accent-blue)', 'var(--accent-purple)', 'var(--accent-orange)',
  'var(--accent-pink)', 'var(--accent-green)', 'var(--accent-red)',
];
function hueOf(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return HUES[h % HUES.length]!;
}
function hueStyle(hue: string): CSSProperties {
  return { '--um-hue': hue } as CSSProperties;
}

function initial(name: string, alt: string): string {
  return (name || alt || '?').trim().charAt(0).toUpperCase();
}

const HHMM = (minute: number): string =>
  `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;

const toMinute = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return (Number.isFinite(h) ? h! : 0) * 60 + (Number.isFinite(m) ? m! : 0);
};

/** 표시용 시각 — `2026-08-20 11:42`. 로캘 서식은 "2026. 8. 20. AM 11:42:00" 이라 안 쓴다.
 *  ⚠ 이 주석에 로캘 문자열을 그대로 적으면 `lib/i18n-locale-hardcode.test.ts` 가 **주석까지**
 *    훑어서 위반으로 잡는다(원본 콘솔에는 그 검사가 없어 그대로 적혀 있었다). 코드는 그대로다. */
function fmtStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const errText = (e: any, fallback: string): string =>
  e?.response?.data?.message ?? e?.response?.data?.error ?? e?.message ?? fallback;

type Sel =
  | { kind: 'all' }
  | { kind: 'cat'; cat: string }
  | { kind: 'res'; id: string };

type ViewFilter = 'all' | 'on' | 'off' | 'appr';

interface Draft {
  /** null = 아직 저장 안 한 신규 자원. */
  id: string | null;
  category: string;
  name: string;
  location: string;
  equipmentInfo: string;
  capacity: number;
  bookingUnit: 'time' | 'day';
  slotMinutes: number;
  open: string;
  close: string;
  requiresApproval: boolean;
  maxDaysAhead: number;
  owner: ReservationManager | null;
  managers: ReservationManager[];
  isActive: boolean;
  sortOrder: number;
  updatedAt: string | null;
}

function toDraft(r: ReservationResource): Draft {
  return {
    id: r.id,
    category: r.category,
    name: r.name,
    location: r.location ?? '',
    equipmentInfo: r.equipmentInfo ?? '',
    capacity: r.capacity,
    bookingUnit: r.bookingUnit,
    slotMinutes: r.slotMinutes,
    open: HHMM(r.openMinute),
    close: HHMM(r.closeMinute),
    requiresApproval: r.requiresApproval,
    maxDaysAhead: r.maxDaysAhead,
    owner: r.owner,
    managers: r.managers,
    isActive: r.isActive,
    sortOrder: r.sortOrder,
    updatedAt: r.updatedAt,
  };
}

function emptyDraft(category: string, sortOrder: number): Draft {
  return {
    id: null, category, name: '', location: '', equipmentInfo: '',
    capacity: 1, bookingUnit: 'time', slotMinutes: 60,
    open: '09:00', close: '18:00',
    requiresApproval: true, maxDaysAhead: 90,
    owner: null, managers: [], isActive: true, sortOrder,
    updatedAt: null,
  };
}

function toInput(d: Draft): ReservationResourceInput {
  return {
    category: d.category.trim(),
    name: d.name.trim(),
    location: d.location.trim() === '' ? null : d.location.trim(),
    equipmentInfo: d.equipmentInfo.trim() === '' ? null : d.equipmentInfo.trim(),
    capacity: d.capacity,
    bookingUnit: d.bookingUnit,
    slotMinutes: d.slotMinutes,
    openMinute: toMinute(d.open),
    closeMinute: toMinute(d.close),
    requiresApproval: d.requiresApproval,
    maxDaysAhead: d.maxDaysAhead,
    ...(d.owner ? { ownerUserId: d.owner.id } : {}),
    // 주담당은 부담당에서 뺀다 — 서버도 같은 정리를 하지만 화면에서도 일관되게 보낸다.
    managerUserIds: d.managers.map((m) => m.id).filter((id) => id !== d.owner?.id),
    isActive: d.isActive,
    sortOrder: d.sortOrder,
  };
}

/** 편집 중인 값이 사용자 화면(8443)에서 어떤 문구가 되는지 — 미리보기 카드의 부제. */
function previewSub(d: Draft): string {
  const bits: string[] = [];
  if (d.location.trim() !== '') bits.push(d.location.trim());
  bits.push(`수량 ${d.capacity}`);
  bits.push(d.requiresApproval ? '승인 필요' : '즉시 확정');
  bits.push(d.bookingUnit === 'day' ? '일 단위' : `${d.slotMinutes}분 단위`);
  return bits.join(' · ');
}

export function ReservationWorkspace() {
  const [list, setList] = useState<ReservationResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [q, setQ] = useState('');
  const [view, setView] = useState<ViewFilter>('all');
  const [closedCats, setClosedCats] = useState<Record<string, boolean>>({});
  const [sel, setSel] = useState<Sel>({ kind: 'all' });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState<null | 'owner' | 'sub'>(null);

  // 끌기 대상은 ref(핸들러에서 즉시 읽어야 함) + state(흐림 표시는 렌더가 필요함) 둘 다 둔다.
  const dragId = useRef<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropHint, setDropHint] = useState<{ id: string; after: boolean } | null>(null);

  const reload = useCallback(async (keepId?: string) => {
    setLoading(true);
    try {
      const rows = await listReservationResources();
      setList(rows);
      setError(null);
      const target = keepId ? rows.find((r) => r.id === keepId) : null;
      if (target) {
        setSel({ kind: 'res', id: target.id });
        setDraft(toDraft(target));
        setDirty(false);
      }
      return rows;
    } catch (e: any) {
      setError(errText(e, '예약 자원을 불러오지 못했습니다.'));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload().then((rows) => {
      // 처음 진입 시 첫 자원을 열어 둔다 — 빈 오른쪽 패널로 시작하지 않게.
      const first = rows[0];
      if (first) { setSel({ kind: 'res', id: first.id }); setDraft(toDraft(first)); setDirty(false); }
    });
  }, [reload]);

  // ── 트리 ─────────────────────────────────────────────────────
  const categories = useMemo(() => {
    const out: string[] = [];
    for (const r of list) if (!out.includes(r.category)) out.push(r.category);
    return out;
  }, [list]);

  const matches = useCallback((r: ReservationResource): boolean => {
    const v = q.trim().toLowerCase();
    if (v !== '') {
      const hit = r.name.toLowerCase().includes(v)
        || r.category.toLowerCase().includes(v)
        || (r.location ?? '').toLowerCase().includes(v)
        || (r.owner?.name ?? '').toLowerCase().includes(v);
      if (!hit) return false;
    }
    if (view === 'on') return r.isActive;
    if (view === 'off') return !r.isActive;
    if (view === 'appr') return r.requiresApproval;
    return true;
  }, [q, view]);

  const rowsOf = useCallback(
    (cat: string) => list.filter((r) => r.category === cat && matches(r)),
    [list, matches],
  );

  const visibleCount = useMemo(() => list.filter(matches).length, [list, matches]);

  // ── 선택 ─────────────────────────────────────────────────────
  function selectResource(r: ReservationResource) {
    if (dirty && !window.confirm('저장하지 않은 변경이 있습니다. 버리고 이동할까요?')) return;
    setSel({ kind: 'res', id: r.id });
    setDraft(toDraft(r));
    setDirty(false);
    setNotice(null); setError(null);
  }

  function selectScope(next: Sel) {
    if (dirty && !window.confirm('저장하지 않은 변경이 있습니다. 버리고 이동할까요?')) return;
    setSel(next);
    setDraft(null);
    setDirty(false);
    setNotice(null); setError(null);
  }

  function startCreate(category?: string) {
    const cat = category ?? (sel.kind === 'cat' ? sel.cat : categories[0] ?? CATEGORY_PRESETS[0]!);
    const next = list.filter((r) => r.category === cat).length + 1;
    setSel({ kind: 'all' });
    setDraft(emptyDraft(cat, next));
    setDirty(true);
    setNotice(null); setError(null);
  }

  const patch = useCallback((p: Partial<Draft>) => {
    setDraft((cur) => (cur ? { ...cur, ...p } : cur));
    setDirty(true);
  }, []);

  // ── 저장 · 삭제 · 활성 ───────────────────────────────────────
  const valid = draft !== null
    && draft.name.trim() !== ''
    && draft.category.trim() !== ''
    && draft.owner !== null
    && toMinute(draft.close) > toMinute(draft.open);

  async function save() {
    if (!draft || !valid) return;
    setSaving(true); setError(null); setNotice(null);
    try {
      const saved = draft.id
        ? await updateReservationResource(draft.id, toInput(draft))
        : await createReservationResource(toInput(draft));
      setNotice(draft.id ? '저장했습니다.' : '자원을 추가했습니다.');
      await reload(saved.id);
    } catch (e: any) {
      setError(errText(e, '저장에 실패했습니다.'));
    } finally {
      setSaving(false);
    }
  }

  function revert() {
    if (!draft) return;
    const row = draft.id ? list.find((r) => r.id === draft.id) : null;
    if (row) { setDraft(toDraft(row)); setDirty(false); }
    else { setDraft(null); setDirty(false); setSel({ kind: 'all' }); }
    setNotice(null); setError(null);
  }

  async function toggleActive() {
    if (!draft) return;
    const next = !draft.isActive;
    if (!draft.id) { patch({ isActive: next }); return; }
    setBusy(true); setError(null);
    try {
      await updateReservationResource(draft.id, { isActive: next });
      setDraft((cur) => (cur ? { ...cur, isActive: next } : cur));
      setList((cur) => cur.map((r) => (r.id === draft.id ? { ...r, isActive: next } : r)));
      setNotice(next ? '다시 노출합니다.' : '사용자 화면에서 내렸습니다. 기존 예약은 그대로 남습니다.');
    } catch (e: any) {
      setError(errText(e, '상태 변경에 실패했습니다.'));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!draft?.id) { setDraft(null); setDirty(false); return; }
    if (!window.confirm(`"${draft.name}" 자원을 삭제할까요? 기존 예약 이력은 남고 새 예약만 막힙니다. 되돌릴 수 없습니다.`)) return;
    setBusy(true); setError(null);
    try {
      await deleteReservationResource(draft.id);
      setDraft(null); setDirty(false); setSel({ kind: 'all' });
      setNotice('삭제했습니다.');
      await reload();
    } catch (e: any) {
      setError(errText(e, '삭제에 실패했습니다.'));
    } finally {
      setBusy(false);
    }
  }

  // ── 순서 끌어 옮기기 ─────────────────────────────────────────
  /** 같은 분류 안에서만 옮긴다. 바뀐 행만 PATCH 한다. */
  async function applyReorder(cat: string, movedId: string, targetId: string, after: boolean) {
    const rows = list.filter((r) => r.category === cat);
    const from = rows.findIndex((r) => r.id === movedId);
    if (from < 0) return;
    const moved = rows[from]!;
    const rest = rows.filter((r) => r.id !== movedId);
    let to = rest.findIndex((r) => r.id === targetId);
    if (to < 0) return;
    if (after) to += 1;
    rest.splice(to, 0, moved);

    const changed = rest
      .map((r, i) => ({ r, order: i + 1 }))
      .filter((x) => x.r.sortOrder !== x.order);
    if (changed.length === 0) return;

    // 낙관적 반영 — 실패하면 reload 가 서버 값으로 되돌린다.
    setList((cur) => cur.map((r) => {
      const hit = changed.find((x) => x.r.id === r.id);
      return hit ? { ...r, sortOrder: hit.order } : r;
    }));
    setBusy(true); setError(null);
    try {
      for (const x of changed) await updateReservationResource(x.r.id, { sortOrder: x.order });
      const rowsNow = await listReservationResources();
      setList(rowsNow);
      const keep = draft?.id ? rowsNow.find((r) => r.id === draft.id) : null;
      if (keep && !dirty) setDraft(toDraft(keep));
    } catch (e: any) {
      setError(errText(e, '순서 변경에 실패했습니다.'));
      await reload();
    } finally {
      setBusy(false);
    }
  }

  // ── 렌더 ─────────────────────────────────────────────────────
  const scope = useMemo(() => {
    if (sel.kind === 'cat') {
      return { name: sel.cat, kind: '분류', hue: hueOf(sel.cat), rows: list.filter((r) => r.category === sel.cat) };
    }
    if (sel.kind === 'res') {
      const r = list.find((x) => x.id === sel.id);
      return {
        name: r?.name ?? '자원', kind: '자원',
        hue: hueOf(r?.category ?? ''), rows: r ? [r] : [],
      };
    }
    return { name: '전체 자원', kind: '회사', hue: 'var(--primary)', rows: list };
  }, [sel, list]);

  function renderResourceNode(r: ReservationResource) {
    const isSel = sel.kind === 'res' && sel.id === r.id;
    const hint = dropHint?.id === r.id ? (dropHint.after ? ' is-dropafter' : ' is-dropbefore') : '';
    return (
      <div
        key={r.id}
        role="treeitem"
        tabIndex={0}
        aria-selected={isSel}
        className={`um-node${isSel ? ' is-sel' : ''}${r.isActive ? '' : ' is-inactive'}${draggingId === r.id ? ' is-dragging' : ''}${hint}`}
        style={hueStyle(hueOf(r.category))}
        draggable
        onDragStart={(e) => { dragId.current = r.id; setDraggingId(r.id); e.dataTransfer.effectAllowed = 'move'; }}
        onDragEnd={() => { dragId.current = null; setDraggingId(null); setDropHint(null); }}
        onDragOver={(e) => {
          // preventDefault 가 없으면 drop 이벤트가 아예 오지 않는다.
          if (!dragId.current || dragId.current === r.id) return;
          const src = list.find((x) => x.id === dragId.current);
          if (!src || src.category !== r.category) return;
          e.preventDefault();
          const box = e.currentTarget.getBoundingClientRect();
          setDropHint({ id: r.id, after: e.clientY > box.top + box.height / 2 });
        }}
        onDragLeave={() => setDropHint((cur) => (cur?.id === r.id ? null : cur))}
        onDrop={(e) => {
          e.preventDefault();
          const moved = dragId.current;
          const after = dropHint?.after ?? false;
          dragId.current = null;
          setDraggingId(null);
          setDropHint(null);
          if (moved && moved !== r.id) void applyReorder(r.category, moved, r.id, after);
        }}
        onClick={() => selectResource(r)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectResource(r); } }}
      >
        <GripVertical size={12} className="ra-grip" aria-hidden />
        <span className="um-mk um-mk--team"><i /></span>
        <span className="um-node__nm">{r.name}</span>
        {!r.isActive && <span className="um-node__off">내림</span>}
        <span className={`ra-flag${r.requiresApproval ? '' : ' ra-flag--instant'}`}>
          {r.requiresApproval ? '승인' : '즉시'}
        </span>
      </div>
    );
  }

  function renderCategoryNode(cat: string) {
    const rows = rowsOf(cat);
    // 검색으로 자식이 모두 걸러진 분류는 감춘다(빈 그룹만 남는 목록 방지).
    if (rows.length === 0 && q.trim() !== '') return null;
    const open = closedCats[cat] !== true;
    const isSel = sel.kind === 'cat' && sel.cat === cat;
    return (
      <div key={cat}>
        <div
          role="treeitem"
          tabIndex={0}
          aria-selected={isSel}
          aria-expanded={open}
          className={`um-node${isSel ? ' is-sel' : ''}`}
          style={hueStyle(hueOf(cat))}
          onClick={() => selectScope({ kind: 'cat', cat })}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectScope({ kind: 'cat', cat }); } }}
        >
          <button
            type="button"
            className={`um-tw${open ? ' is-open' : ''}`}
            aria-label={open ? '접기' : '펼치기'}
            onClick={(e) => { e.stopPropagation(); setClosedCats((p) => ({ ...p, [cat]: open })); }}
          >
            <ChevronRight size={12} />
          </button>
          <span className="um-mk um-mk--dept"><i /></span>
          <span className="um-node__nm">{cat}</span>
          <span className="um-node__n">{rows.length}</span>
        </div>
        {open && rows.length > 0 && <div className="um-kids">{rows.map(renderResourceNode)}</div>}
      </div>
    );
  }

  const scopeStats = useMemo(() => {
    const rows = scope.rows;
    const on = rows.filter((r) => r.isActive).length;
    return {
      total: rows.length,
      on,
      off: rows.length - on,
      appr: rows.filter((r) => r.requiresApproval).length,
      day: rows.filter((r) => r.bookingUnit === 'day').length,
    };
  }, [scope]);

  return (
    <div className="um-ws ra-ws">
      {/* ══════════ 좌: 분류 › 자원 ══════════ */}
      <aside className="um-panel um-panel--org">
        <div className="um-ph">
          <ClipboardCheck size={15} />
          <h3>분류 · 자원</h3>
          <span className="um-ph__n">{categories.length}분류 · {list.length}자원</span>
          <span className="um-ph__sp" />
        </div>

        <div className="um-org__tools">
          <SearchInput
            value={q}
            placeholder="분류 · 예약명 · 담당자 검색"
            aria-label="자원 검색"
            onChange={(e) => setQ(e.target.value)}
            onClear={() => setQ('')}
            fullWidth
          />
          <Select
            value={view}
            onChange={(v) => setView(v as ViewFilter)}
            ariaLabel="표시 상태"
            options={[
              { value: 'all', label: '전체 보기' },
              { value: 'on', label: '활성만' },
              { value: 'off', label: '내린 것만' },
              { value: 'appr', label: '승인 필요만' },
            ]}
          />
        </div>

        <nav className="um-tree" role="tree" aria-label="분류 · 자원">
          <div
            role="treeitem"
            tabIndex={0}
            aria-selected={sel.kind === 'all'}
            className={`um-node um-node--root${sel.kind === 'all' ? ' is-sel' : ''}`}
            onClick={() => selectScope({ kind: 'all' })}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectScope({ kind: 'all' }); } }}
          >
            <span className="um-tw is-leaf" />
            <span className="um-mk um-mk--company"><i /></span>
            <span className="um-node__nm">전체 자원</span>
            <span className="um-node__n">{visibleCount}</span>
          </div>

          <div className="um-kids">{categories.map(renderCategoryNode)}</div>

          {loading && list.length === 0 && (
            <p className="um-f__hint" style={{ padding: '10px 9px' }}>불러오는 중…</p>
          )}
          {!loading && list.length === 0 && (
            <p className="um-f__hint" style={{ padding: '10px 9px' }}>
              등록된 예약 자원이 없습니다. 아래 <strong>자원 추가</strong> 로 시작하세요.
            </p>
          )}
          {!loading && list.length > 0 && visibleCount === 0 && (
            <p className="um-f__hint" style={{ padding: '10px 9px' }}>조건에 맞는 자원이 없습니다.</p>
          )}
        </nav>

        <div className="um-orgcard">
          <div className="um-orgcard__t" style={hueStyle(scope.hue)}>
            <b>{scope.name}</b>
            <span className="um-orgcard__kind">{scope.kind}</span>
          </div>
          <dl>
            <dt>자원</dt><dd>{scopeStats.total}개</dd>
            <dt>활성 / 내림</dt><dd>{scopeStats.on} / {scopeStats.off}</dd>
            <dt>승인 필요</dt><dd>{scopeStats.appr}개</dd>
            <dt>예약 단위</dt><dd>시간 {scopeStats.total - scopeStats.day} · 일 {scopeStats.day}</dd>
          </dl>
          <div className="um-orgcard__act">
            <Button
              variant="primary"
              size="compact"
              iconLeft={<Plus size={13} aria-hidden />}
              onClick={() => startCreate(sel.kind === 'cat' ? sel.cat : undefined)}
            >
              자원 추가
            </Button>
          </div>
        </div>
      </aside>

      {/* ══════════ 우: 자원 편집 ══════════ */}
      <section className="um-panel um-panel--user">
        {!draft ? (
          <>
            <div className="um-ph">
              <span className="um-crumb"><b>{scope.name}</b></span>
              <span className="um-ph__sp" />
              <span className="um-ph__n">{scopeStats.total}자원</span>
            </div>
            <div className="um-blank">
              <Users size={26} aria-hidden />
              <b>왼쪽에서 자원을 고르세요</b>
              <p>
                분류를 고르면 그 분류의 요약이 왼쪽 아래에 뜹니다. 자원을 고르면 여기에서 바로 고칩니다 —
                따로 창이 열리지 않습니다.
              </p>
              <Button variant="primary" size="compact" iconLeft={<Plus size={13} aria-hidden />}
                onClick={() => startCreate()}>
                자원 추가
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="um-ph">
              <span className="um-crumb">
                <span style={{ color: 'var(--text-subtle)', fontWeight: 600 }}>{draft.category || '분류'}</span>
                <span style={{ color: 'var(--text-subtle)' }}>›</span>
                <b>{draft.name.trim() === '' ? '새 예약 자원' : draft.name}</b>
              </span>
              <span className="um-ph__sp" />
              <button
                type="button"
                className={`ra-sw${draft.isActive ? ' is-on' : ''}`}
                aria-pressed={draft.isActive}
                disabled={busy}
                onClick={() => void toggleActive()}
              >
                <span className="ra-sw__t" />
                {draft.isActive ? '활성' : '비활성'}
              </button>
              <Button
                variant="quiet-danger"
                size="compact"
                disabled={busy || saving}
                onClick={() => void remove()}
                iconLeft={<Trash2 size={13} aria-hidden />}
              >
                삭제
              </Button>
            </div>

            <div className="ra-ed">
              {error && <div className="ra-note ra-note--warn" role="alert"><Info size={14} aria-hidden /><div>{error}</div></div>}
              {notice && !error && <div className="ra-note" role="status"><Check size={14} aria-hidden /><div>{notice}</div></div>}

              {/* ── 기본 ── */}
              <div className="ra-sec">
                <div className="ra-sec__h">기본<span className="sp" /></div>
                <div className="um-fields">
                  <FormField label="분류" htmlFor="ra-cat" required hint="사용자 화면 좌측 목록의 그룹 이름입니다.">
                    <Input
                      list="ra-cat-presets"
                      value={draft.category}
                      onChange={(e) => patch({ category: e.target.value })}
                      fullWidth
                    />
                    <datalist id="ra-cat-presets">
                      {[...new Set([...categories, ...CATEGORY_PRESETS])].map((c) => <option key={c} value={c} />)}
                    </datalist>
                  </FormField>
                  <FormField label="예약명" htmlFor="ra-name" required>
                    <Input
                      maxLength={100}
                      value={draft.name}
                      onChange={(e) => patch({ name: e.target.value })}
                      placeholder="예: 대회의실 / 빔프로젝터 1호기"
                      fullWidth
                    />
                  </FormField>
                  <FormField label="위치" htmlFor="ra-loc">
                    <Input
                      maxLength={200}
                      value={draft.location}
                      onChange={(e) => patch({ location: e.target.value })}
                      placeholder="예: 본관 9층"
                      fullWidth
                    />
                  </FormField>
                  <FormField label="목록 순서" htmlFor="ra-order" hint="같은 분류 안에서의 순서 — 사용자 화면에 그대로 반영됩니다.">
                    <div className="um-row2">
                      <span style={{ maxWidth: 116 }}>
                        <NumberInput
                          value={draft.sortOrder}
                          min={0}
                          max={9999}
                          onValueChange={(v) => patch({ sortOrder: Math.max(0, v ?? 0) })}
                          fullWidth
                        />
                      </span>
                      <span>왼쪽 트리에서 끌어 옮겨도 됩니다</span>
                    </div>
                  </FormField>
                  <FormField label="장비정보" htmlFor="ra-info" className="w2">
                    <Textarea
                      rows={2}
                      maxLength={1000}
                      value={draft.equipmentInfo}
                      onChange={(e) => patch({ equipmentInfo: e.target.value })}
                      placeholder="모델명·구성품·주의사항 등"
                      fullWidth
                    />
                  </FormField>
                </div>
              </div>

              {/* ── 운영 규칙 ── */}
              <div className="ra-sec">
                <div className="ra-sec__h">운영 규칙<span className="sp" /></div>
                <div className="um-fields um-fields--3">
                  <div className="um-f">
                    <label>예약 단위</label>
                    <div className="ra-seg" role="group" aria-label="예약 단위">
                      <button type="button" className={draft.bookingUnit === 'time' ? 'is-on' : ''}
                        onClick={() => patch({ bookingUnit: 'time' })}>시간</button>
                      <button type="button" className={draft.bookingUnit === 'day' ? 'is-on' : ''}
                        onClick={() => patch({ bookingUnit: 'day' })}>일</button>
                    </div>
                    <span className="um-f__hint">회의실·장비는 시간, 도서·차량 대여는 일.</span>
                  </div>
                  <div className="um-f">
                    <label>승인 방식</label>
                    <div className="ra-seg" role="group" aria-label="승인 방식">
                      <button type="button" className={draft.requiresApproval ? '' : 'is-on'}
                        onClick={() => patch({ requiresApproval: false })}>즉시 확정</button>
                      <button type="button" className={draft.requiresApproval ? 'is-on' : ''}
                        onClick={() => patch({ requiresApproval: true })}>담당자 승인</button>
                    </div>
                    <span className="um-f__hint">승인으로 두면 담당자에게 알림이 갑니다.</span>
                  </div>
                  <FormField label="보유 수량" htmlFor="ra-cap" hint="같은 시간대에 이 수량까지 동시 예약.">
                    <NumberInput
                      value={draft.capacity}
                      min={1}
                      max={9999}
                      onValueChange={(v) => patch({ capacity: Math.max(1, v ?? 1) })}
                      fullWidth
                    />
                  </FormField>

                  {/* 일 단위여도 자리를 지우지 않는다 — 흐려질 뿐(폼 높이 고정). */}
                  <FormField
                    label="운영 시간"
                    className={draft.bookingUnit === 'day' ? 'is-muted' : undefined}
                    /* ★ 「끝이 시작보다 앞」은 힌트가 아니라 **오류**다 — 칸 테두리까지 붉어져야 한다. */
                    error={toMinute(draft.close) <= toMinute(draft.open) ? '종료 시각이 시작 시각보다 뒤여야 합니다.' : undefined}
                  >
                    <div className="um-row2">
                      <TimePicker
                        value={draft.open}
                        onChange={(v) => patch({ open: v ?? '' })}
                        minuteStep={30}
                        ariaLabel="운영 시작"
                      />
                      <span>~</span>
                      <TimePicker
                        value={draft.close}
                        onChange={(v) => patch({ close: v ?? '' })}
                        minuteStep={30}
                        ariaLabel="운영 종료"
                      />
                    </div>
                  </FormField>
                  <FormField
                    label="시간 단위"
                    htmlFor="ra-slot"
                    hint="신청 격자가 이 단위로 끊깁니다."
                    className={draft.bookingUnit === 'day' ? 'is-muted' : undefined}
                  >
                    <Select
                      value={String(draft.slotMinutes)}
                      onChange={(v) => patch({ slotMinutes: Number(v) })}
                      options={[30, 60, 120].map((m) => ({ value: String(m), label: `${m}분` }))}
                    />
                  </FormField>
                  <FormField label="예약 가능 기간" htmlFor="ra-ahead">
                    <div className="um-row2">
                      <span style={{ maxWidth: 124 }}>
                        <NumberInput
                          value={draft.maxDaysAhead}
                          min={1}
                          max={730}
                          onValueChange={(v) => patch({ maxDaysAhead: Math.max(1, v ?? 1) })}
                          fullWidth
                        />
                      </span>
                      <span>일 뒤까지</span>
                    </div>
                  </FormField>
                </div>
              </div>

              {/* ── 담당자 ── */}
              <div className="ra-sec">
                <div className="ra-sec__h">담당자<span className="sp" /></div>
                <div className="um-fields">
                  <div className="um-f">
                    <label>주담당 <em>*</em></label>
                    <div className="ra-owner">
                      {draft.owner ? (
                        <>
                          <span className="um-av" style={hueStyle(hueOf(draft.owner.id))} aria-hidden>
                            {initial(draft.owner.name, '')}
                          </span>
                          <span className="ra-owner__b">
                            <b>{draft.owner.name}</b>
                            <span>{draft.owner.position ?? draft.owner.email ?? '-'}</span>
                          </span>
                        </>
                      ) : (
                        <span className="ra-owner__b"><b style={{ color: 'var(--text-subtle)' }}>지정 안 됨</b></span>
                      )}
                      <Button variant="secondary" size="compact" onClick={() => setPicker('owner')}>
                        {draft.owner ? '변경' : '지정'}
                      </Button>
                    </div>
                    <span className="um-f__hint">사용자 화면에 사진·이름·직책이 뜨고 신청 알림을 받습니다.</span>
                  </div>
                  <div className="um-f">
                    <label>
                      부담당
                      <span style={{ fontWeight: 600, color: 'var(--text-subtle)' }}>
                        {draft.managers.filter((m) => m.id !== draft.owner?.id).length}명
                      </span>
                    </label>
                    <div className="ra-chips">
                      {draft.managers.filter((m) => m.id !== draft.owner?.id).map((m) => (
                        <span key={m.id} className="ra-chip">
                          <span className="um-av" style={hueStyle(hueOf(m.id))} aria-hidden>{initial(m.name, '')}</span>
                          {m.name}
                          <button
                            type="button"
                            aria-label={`${m.name} 제외`}
                            onClick={() => patch({ managers: draft.managers.filter((x) => x.id !== m.id) })}
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                      <button type="button" className="ra-chip ra-chip--add" onClick={() => setPicker('sub')}>
                        <Plus size={12} /> 추가
                      </button>
                    </div>
                    <span className="um-f__hint">부담당도 승인처리 메뉴와 신청 알림을 받습니다.</span>
                  </div>
                  <div className="um-f w2">
                    <div className="ra-note">
                      <Info size={14} aria-hidden />
                      <div>
                        <b>담당자는 조직도에서 찾습니다.</b> 부서 › 팀으로 좁혀 들어가 고르므로
                        사용자가 100명을 넘는 회사에서도 끝까지 도달합니다.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── 미리보기 ── */}
              <div className="ra-sec">
                <div className="ra-sec__h">사용자 화면 미리보기<span className="sp" /></div>
                <div className="ra-prev">
                  <div className="ra-prevcard">
                    <div className="ra-prevcard__n">{draft.name.trim() === '' ? '예약명' : draft.name}</div>
                    <div className="ra-prevcard__s">{previewSub(draft)}</div>
                    {draft.bookingUnit === 'time' && (
                      <div className="ra-prevcard__l"><i />지금 가능 · {draft.close} 까지</div>
                    )}
                  </div>
                  <p className="ra-prevnote">
                    <b>8443 “예약하기” 좌측 목록에 이렇게 뜹니다.</b> 위에서 고친 분류·이름·위치·수량·승인 방식이
                    그대로 이 카드의 문구가 됩니다. 목록 순서도 왼쪽 트리에서 매긴 순서 그대로입니다.
                    {draft.bookingUnit === 'time' && (
                      <>
                        {' '}아래 “지금 가능” 줄은 사용자 화면이 <b>그 시점에 계산</b>하는 값이라
                        여기서는 운영 종료 시각을 쓴 예시만 보여 줍니다.
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="um-foot">
              <span style={{ fontFamily: 'var(--um-mono)', color: 'var(--text-subtle)' }}>
                {draft.id
                  ? (draft.updatedAt ? `마지막 저장 ${fmtStamp(draft.updatedAt)}` : '저장 이력 없음')
                  : '아직 저장하지 않은 새 자원입니다'}
              </span>
              {dirty && <b style={{ color: 'var(--color-warning)' }}>변경 있음</b>}
              <span style={{ flex: 1 }} />
              <Button variant="secondary" size="compact" disabled={!dirty || saving} onClick={revert}>
                되돌리기
              </Button>
              <Button
                variant="primary"
                size="compact"
                disabled={!valid || saving || busy}
                loading={saving}
                onClick={() => void save()}
              >
                {saving ? '저장 중…' : draft.id ? '저장' : '추가'}
              </Button>
            </div>
          </>
        )}
      </section>

      {picker && draft && (
        <OrgPicker
          mode={picker}
          selectedIds={picker === 'owner'
            ? (draft.owner ? [draft.owner.id] : [])
            : draft.managers.map((m) => m.id)}
          onClose={() => setPicker(null)}
          onPick={(m) => {
            if (picker === 'owner') {
              patch({ owner: m, managers: draft.managers.filter((x) => x.id !== m.id) });
              setPicker(null);
              return;
            }
            const has = draft.managers.some((x) => x.id === m.id);
            patch({ managers: has ? draft.managers.filter((x) => x.id !== m.id) : [...draft.managers, m] });
          }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   담당자 조직도 피커 — 부서 › 팀 › 팀원.

   옛 화면은 `listUsers({limit:100})` 을 체크박스로 뿌렸는데 listUsers 에 검색이 없어
   101번째 사람은 지정할 방법이 아예 없었다. 여기서는 사용자 관리가 이미 쓰는
   조직도 API 로 좁혀 들어간다(신규 서버 API 0건). 팀에 속하지 않은 사람을 위해
   "전체 사용자" 항목을 두고 cursor 로 이어 받는다.
   ══════════════════════════════════════════════════════════════════════════ */

const ALL_USERS = '__all';

function OrgPicker({ mode, selectedIds, onPick, onClose }: {
  mode: 'owner' | 'sub';
  selectedIds: string[];
  onPick: (m: ReservationManager) => void;
  onClose: () => void;
}) {
  const [divs, setDivs] = useState<OrgDivision[]>([]);
  const [teams, setTeams] = useState<OrgTeam[]>([]);
  const [teamId, setTeamId] = useState<string>(ALL_USERS);
  const [people, setPeople] = useState<ReservationManager[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  useEffect(() => {
    void Promise.all([listDivisions(), listTeams()])
      .then(([d, t]) => { setDivs(d); setTeams(t); })
      .catch(() => { setDivs([]); setTeams([]); });
  }, []);

  const loadPeople = useCallback(async (id: string, more?: string) => {
    setLoading(true); setErr(null);
    try {
      if (id === ALL_USERS) {
        const res = await listUsers({ limit: 50, ...(more ? { cursor: more } : {}) });
        const rows = res.users.map(toManager);
        setPeople((cur) => (more ? [...cur, ...rows] : rows));
        setCursor(res.nextCursor);
      } else {
        const rows = await listTeamMembers(id);
        setPeople(rows.map((m) => ({
          id: m.user.id, name: m.user.name, position: m.user.position, avatarUrl: null, email: m.user.email,
        })));
        setCursor(null);
      }
    } catch (e: any) {
      setErr(errText(e, '사용자를 불러오지 못했습니다.'));
      if (!more) setPeople([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadPeople(teamId); }, [teamId, loadPeople]);

  const shown = useMemo(() => {
    const v = q.trim().toLowerCase();
    if (v === '') return people;
    return people.filter((p) =>
      p.name.toLowerCase().includes(v)
      || (p.email ?? '').toLowerCase().includes(v)
      || (p.position ?? '').toLowerCase().includes(v));
  }, [people, q]);

  const teamsOf = (divisionId: string | null) =>
    teams.filter((t) => (divisionId === null ? t.divisionId === null : t.divisionId === divisionId));

  function teamRow(t: OrgTeam) {
    return (
      <button
        key={t.id}
        type="button"
        className={`ra-pick__row${teamId === t.id ? ' is-sel' : ''}`}
        onClick={() => setTeamId(t.id)}
      >
        <span className="um-mk um-mk--team" style={hueStyle(hueOf(t.divisionId ?? t.id))}><i /></span>
        <b>{t.name}</b>
        <span className="sp" />
        <span>{t.memberCount}</span>
      </button>
    );
  }

  return (
    <div className="um-ws ra-ws">
      <div className="ra-pick__scrim" role="presentation" onClick={onClose}>
        <div
          className="ra-pick"
          role="dialog"
          aria-modal="true"
          aria-label={mode === 'owner' ? '주담당 지정' : '부담당 추가'}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="um-ph">
            <Users size={15} />
            <h3>{mode === 'owner' ? '주담당 지정' : '부담당 추가'}</h3>
            <span className="um-ph__sp" />
            <Button variant="secondary" size="compact" onClick={onClose} iconLeft={<X size={14} aria-hidden />}>
              닫기
            </Button>
          </div>

          <div className="um-bar">
            <SearchInput
              value={q}
              placeholder="이름 · 이메일 · 직책"
              aria-label="사용자 검색"
              onChange={(e) => setQ(e.target.value)}
              onClear={() => setQ('')}
              wrapperClassName="ra-search"
            />
            {mode === 'sub' && (
              <span style={{ fontSize: '0.77rem', color: 'var(--text-muted)' }}>
                여러 명을 눌러 담고, 닫으면 반영됩니다.
              </span>
            )}
          </div>

          <div className="ra-pick__b">
            <div className="ra-pick__l">
              <button
                type="button"
                className={`ra-pick__row${teamId === ALL_USERS ? ' is-sel' : ''}`}
                onClick={() => setTeamId(ALL_USERS)}
              >
                <span className="um-mk um-mk--company"><i /></span>
                <b>전체 사용자</b>
              </button>
              {divs.map((d) => (
                <div key={d.id}>
                  <div style={{ padding: '10px 9px 4px', fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.04em', color: 'var(--text-subtle)' }}>
                    {d.name}
                  </div>
                  {teamsOf(d.id).map(teamRow)}
                </div>
              ))}
              {teamsOf(null).length > 0 && (
                <div>
                  <div style={{ padding: '10px 9px 4px', fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.04em', color: 'var(--text-subtle)' }}>
                    부서 없음
                  </div>
                  {teamsOf(null).map(teamRow)}
                </div>
              )}
            </div>

            <div className="ra-pick__r">
              {err && <p className="ra-pick__hint um-warn">{err}</p>}
              {loading && people.length === 0 && <p className="ra-pick__hint">불러오는 중…</p>}
              {!loading && shown.length === 0 && !err && (
                <p className="ra-pick__hint">
                  {q.trim() === '' ? '이 팀에 속한 사람이 없습니다.' : '검색 결과가 없습니다.'}
                </p>
              )}
              {shown.map((p) => {
                const on = selectedIds.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`ra-pick__row${on ? ' is-sel' : ''}`}
                    onClick={() => onPick(p)}
                  >
                    <span className="um-av" style={hueStyle(hueOf(p.id))} aria-hidden>{initial(p.name, p.email ?? '')}</span>
                    <b>{p.name}</b>
                    <span>{p.position ?? p.email ?? ''}</span>
                    <span className="sp" />
                    {on && <Check size={15} className="ra-pick__on" aria-label="선택됨" />}
                  </button>
                );
              })}
              {cursor && (
                <Button
                  variant="secondary"
                  size="compact"
                  style={{ margin: '8px auto', display: 'flex' }}
                  disabled={loading}
                  loading={loading}
                  onClick={() => void loadPeople(teamId, cursor)}
                >
                  더 보기
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function toManager(u: UserListItem): ReservationManager {
  return { id: u.id, name: u.name, position: u.position, avatarUrl: u.avatarUrl, email: u.email };
}
